/**
 * The enumerations the API and the client share. These mirror
 * Backend/src/3-models/enums.ts exactly; the two files are the contract, and a
 * value added to one has to be added to the other.
 */

export const LessonStatus = {
  Paid: 'paid',
  Unpaid: 'unpaid',
  CancelledCharged: 'cancelled_charged',
  CancelledFree: 'cancelled_free'
} as const;

export type LessonStatus = (typeof LessonStatus)[keyof typeof LessonStatus];

export const LESSON_STATUSES: readonly LessonStatus[] = Object.freeze(Object.values(LessonStatus));

export function isLessonStatus(value: unknown): value is LessonStatus {
  return typeof value === 'string' && (LESSON_STATUSES as readonly string[]).includes(value);
}

export const SubscriptionStatus = {
  Free: 'free',
  Premium: 'premium',
  Trial: 'trial',
  Expired: 'expired',
  Cancelled: 'cancelled'
} as const;

export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];

export const Plan = { Free: 'free', Premium: 'premium' } as const;
export type Plan = (typeof Plan)[keyof typeof Plan];

export const PremiumFeature = {
  Receipts: 'RECEIPTS',
  AdvancedReports: 'ADVANCED_REPORTS',
  Exports: 'EXPORTS',
  Backups: 'BACKUPS',
  BusinessBranding: 'BUSINESS_BRANDING',
  PaymentReminders: 'PAYMENT_REMINDERS',
  StudentProfile: 'STUDENT_PROFILE',
  IntakeForms: 'INTAKE_FORMS'
} as const;

export type PremiumFeature = (typeof PremiumFeature)[keyof typeof PremiumFeature];

/**
 * What a row in the roster actually is. An organization is a body that pays
 * without being taught: a conservatory, a music programme, a community centre.
 * It uses the same row as a student, so debt, reminders and receipts work for
 * it unchanged, but it is not counted as a student and is not taught.
 */
export const StudentKind = {
  Student: 'student',
  Organization: 'organization'
} as const;

export type StudentKind = (typeof StudentKind)[keyof typeof StudentKind];

/**
 * How one ensemble meeting turns into money. See the Backend enum for the
 * full rules; the one worth remembering here is that `institution` charges an
 * organization and nobody in the room, which is how most ensemble teaching is
 * actually paid for.
 */
export const EnsembleBilling = {
  PerStudent: 'per_student',
  Split: 'split',
  SinglePayer: 'single_payer',
  Institution: 'institution'
} as const;

export type EnsembleBilling = (typeof EnsembleBilling)[keyof typeof EnsembleBilling];

export const ENSEMBLE_BILLINGS: readonly EnsembleBilling[] = Object.freeze(
  Object.values(EnsembleBilling)
);

/** The two modes billed to one party rather than divided among the members. */
export function billingHasPayer(mode: EnsembleBilling): boolean {
  return mode === EnsembleBilling.SinglePayer || mode === EnsembleBilling.Institution;
}

/** The modes that need a price for the group as a whole. */
export function billingHasGroupPrice(mode: EnsembleBilling): boolean {
  return mode !== EnsembleBilling.PerStudent;
}

/** Sunday is 0, matching Date.getDay and the stored weekday column. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const SUPPORTED_LANGUAGES = ['he', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export type Direction = 'rtl' | 'ltr';
export type Theme = 'light' | 'dark';
